import React from 'react';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  CustomButton,
  TableBodySkeleton,
} from '../../../common/components';

/**
 * Tabla de datos para contratos archivados
 * Muestra los contratos archivados organizados por planes y grupos
 */
const ArchivedContractsDataTable = ({
  archivedContractsCount,
  plans,
  groups,
  onEditContracts,
  onOpenDialog,
  isLoading,
  LoadingComponent,
}) => {
  /**
   * Verifica si hay contratos archivados (con o sin suscriptores) para un plan
   */
  const hasArchivedContracts = (planUuid, isPremium = false) => {
    if (!archivedContractsCount) return false;

    return groups.some((group) => {
      const withSubsGroupCounts = archivedContractsCount.with_subscribers?.[group.uuid] || {};
      const withoutSubsGroupCounts = archivedContractsCount.without_subscribers?.[group.uuid] || {};

      const withSubsCount = isPremium
        ? withSubsGroupCounts.premium_count || 0
        : withSubsGroupCounts.regular_count || 0;

      const withoutSubsCount = isPremium
        ? withoutSubsGroupCounts.premium_count || 0
        : withoutSubsGroupCounts.regular_count || 0;

      const totalCount = withSubsCount + withoutSubsCount;

      return group.uuid_plan === planUuid && totalCount > 0;
    });
  };

  /**
   * Maneja el click para editar contratos archivados (con o sin suscriptores)
   */
  const handleArchivedContractsClick = async (planUuid, isPremium) => {
    onOpenDialog();
    const filters = {
      archived: true,
      uuid_plan: planUuid,
      is_premium: isPremium,
    };

    await onEditContracts(filters);
  };

  /**
   * Renderiza una fila de plan archivado (regular o premium)
   */
  const renderPlanRow = (plan, isPremium) => {
    const planName = isPremium ? `${plan.name} Premium` : plan.name;

    return (
      <TableRow key={`archived-${plan.uuid}-${isPremium ? 'premium' : 'regular'}`}>
        <RowTableCell>{planName}</RowTableCell>

        <RowTableCell>
          {groups.map((group, key) => {
            if (group.uuid_plan !== plan.uuid) return null;

            const withSubsGroupCounts = archivedContractsCount?.with_subscribers?.[group.uuid] || {};
            const withoutSubsGroupCounts = archivedContractsCount?.without_subscribers?.[group.uuid] || {};

            const withSubsCount = isPremium
              ? withSubsGroupCounts.premium_count || 0
              : withSubsGroupCounts.regular_count || 0;

            const withoutSubsCount = isPremium
              ? withoutSubsGroupCounts.premium_count || 0
              : withoutSubsGroupCounts.regular_count || 0;

            const totalContratos = withSubsCount + withoutSubsCount;

            if (totalContratos > 0) {
              return (
                <React.Fragment key={key}>
                  <strong>{group.name}</strong>: {totalContratos}
                  <br />
                </React.Fragment>
              );
            }
            return null;
          })}
        </RowTableCell>

        <RowTableCell sx={{ textAlign: 'center' }}>
          <CustomButton
            variant="base"
            disabled={!hasArchivedContracts(plan.uuid, isPremium)}
            onClick={() => handleArchivedContractsClick(plan.uuid, isPremium)}
            buttonIcon={EditIcon}
            sx={{ paddingX: 5 }}
          />
        </RowTableCell>
      </TableRow>
    );
  };

  return (
    <GeneralTableBox hasMaxHeight={false} showHover={false} showZebra={false}>
      <Table aria-label="archived contracts table">
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="25%">Nombre del plan</HeadTableCell>
            <HeadTableCell wmd="65%">Grupo</HeadTableCell>
            <HeadTableCell wmd="10%">Editar</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableBodySkeleton />
          ) : (
            plans.flatMap((plan) => [renderPlanRow(plan, false), renderPlanRow(plan, true)])
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default ArchivedContractsDataTable;
